// DS: CO5.1.1
import React, { useEffect } from 'react';
import { compose } from 'redux';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { BiData, BuyCoreButtonBiEvent } from '@wix/da-bi/pkg/events';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import { CoreLevel } from '@wix/da-react-context/pkg/publicSession';
import DSModal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import { MAX_TITLE_LENGTH } from '../../../utils/isValidFolderName';
import { useTranslation } from 'react-i18next';
import withCurrentMediaSource, {
  WithCurrentMediaSourceProps,
} from '../../_hocs/withCurrentMediaSource';
import withCurrentGallection, {
  WithCurrentGallectionProps,
} from '../../_hocs/withCurrentGallection';
import withGallections, {
  WithGallectionsProps,
} from '../../_hocs/withGallections';
import withCurrentGallectionSubfolders, {
  WithCurrentGallectionSubfoldersProps,
} from '../../_hocs/withCurrentGallectionSubfolders';
import { connectMediaStore } from '../../_hocs/withOwnReduxStore';
import { createGallection, CreateGallectionPayload } from '../../actions';
import PremiumForm from '../../../forms/PremiumForm';
import { PremiumFolderType } from '../../../types/gallection';
import yupResolver from '../../../utils/yupResolver';
import { dollarsToPoints } from '../../../utils/points';

import { MediaSourceType } from '../../types';
import {
  getCoreLevel,
  getCurrentUserHasCore,
} from '../../../publicSession/selectors';
import useValidation from './useValidation';
import { FormValues } from './types';
import DisclosureBanner from '@wix/da-ds/pkg/DisclosureBanner';
import BuyCoreBrandedButton from '../../../Button/core/dsPresets/BuyCoreBrandedButton/BuyCoreBrandedButton';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import withGruser, { WithGruserProps } from '../../_hocs/withGruser';
import { isGroup as getIsGroup } from '../../../User/helpers';

import newGallectionPopupStyles from './NewGallectionPopup.scss';

export type OwnProps = {
  onCancel?: () => void;
  onDone?: () => void;
};

export type Props = OwnProps &
  WithCurrentMediaSourceProps &
  WithGruserProps &
  WithCurrentGallectionProps &
  WithCurrentGallectionSubfoldersProps &
  WithGallectionsProps & {
    createGallection(payload: CreateGallectionPayload): any;
    coreLevel: CoreLevel;
    hasCoreMembership: boolean;
  };

export const NewGallectionPopup: React.FC<Props> = ({
  currentMediaSource,
  currentFolderId,
  gruser,
  subfolders,
  gallections: topLevelGallections,
  coreLevel,
  hasCoreMembership,
  onCancel,
  onDone,
  createGallection: createGallectionAction,
}) => {
  const { t } = useTranslation();
  const isGallery = currentMediaSource === MediaSourceType.GALLERY;
  const isPrivateCollection =
    currentMediaSource === MediaSourceType.PRIVATE_COLLECTION;

  const existingFolderNames = (
    currentFolderId ? subfolders : topLevelGallections
  ).map(gallection => gallection.name);

  const validationSchema = useValidation(
    coreLevel,
    isGallery,
    existingFolderNames
  );
  const formContext = useForm<FormValues>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema, {
      abortEarly: false,
    }),
    defaultValues: {
      title: '',
      price: '',
      folderType: PremiumFolderType.Regular,
    },
  });

  const { register, formState, control, errors, getValues, handleSubmit } =
    formContext;

  useEffect(() => {
    register('folderType');
  }, [register]);

  const isGroup = gruser && getIsGroup(gruser);

  const withPremiumForm =
    !isGroup &&
    currentMediaSource === MediaSourceType.GALLERY &&
    !currentFolderId; /* PF can not be a sub-folder */

  const onSubmit = data => {
    createGallectionAction({
      name: data.title,
      priceInPoints: dollarsToPoints(data.price),
      folderType: data.folderType,
    });
    onDone && onDone();
  };

  const onTitleKeyUp = (ev: React.KeyboardEvent<HTMLInputElement>) => {
    if (formState.isValid && ev.key === 'Enter') {
      handleSubmit(onSubmit);
    }
  };

  const newFolderKeyPart = currentFolderId ? 'newSubGallery' : 'new';

  const heading = t(
    `mediapopup.gallections.${currentMediaSource}.prompt.${newFolderKeyPart}.title`
  );

  let subheading = '';
  if (isPrivateCollection) {
    subheading = t('addToCollectionPopover.private.subtitle');
  }

  const folderType = getValues('folderType');
  const isWatcherOnlyFolder = folderType === PremiumFolderType.Watchers;
  const isPaidFolder = folderType === PremiumFolderType.Paid;
  const showBuyCoreCTAasPrimaryButton =
    !hasCoreMembership &&
    (isPrivateCollection || isWatcherOnlyFolder || isPaidFolder);

  let buyCoreReferralInfo = isWatcherOnlyFolder
    ? ReferralType.PAYWALL_WATCHERS_ONLY
    : ReferralType.PAYWALL_PG;
  if (isPrivateCollection) {
    buyCoreReferralInfo = ReferralType.PRIVATE_COLLECTIONS_POPUP;
  }

  return (
    <DSModal
      isOpen
      onRequestClose={onCancel}
      className={newGallectionPopupStyles['root']}
      contentClassName={newGallectionPopupStyles['content']}
      heading={heading}
      subheading={subheading}
      showHeaderBorder
      showFooterBorder
      footerRight={[
        <TextButton key="cancel" onClick={onCancel} size="large" variant="main">
          {t('popup.cancel')}
        </TextButton>,
        showBuyCoreCTAasPrimaryButton ? (
          <BuyCoreBrandedButton
            size="large"
            withSaleSparkles
            biData={BiData<BuyCoreButtonBiEvent>({
              evid: 375,
              referral_info: buyCoreReferralInfo,
            })}
          >
            {t('common.upgradeToCoreCTA')}
          </BuyCoreBrandedButton>
        ) : (
          <BrandedButton
            key="submit"
            size="large"
            variant="brand-green"
            disabled={formState.isSubmitting}
            onClick={handleSubmit(onSubmit)}
          >
            {t('mediapopup.create')}
          </BrandedButton>
        ),
      ]}
    >
      <div>
        {isPrivateCollection && !hasCoreMembership && (
          <DisclosureBanner
            className={
              newGallectionPopupStyles[
                'non-core-banner-for-private-collections'
              ]
            }
            message={t(
              'mediapopup.newFolderPopup.gallery.nonCoreBanner.message'
            )}
            ctaLabel={t('common.upgradeCoreCTA')}
            ctaBiData={BiData<BuyCoreButtonBiEvent>({
              evid: 375,
              referral_info: ReferralType.PRIVATE_COLLECTIONS_POPUP,
            })}
          />
        )}
        <Controller
          name="title"
          control={control}
          render={({ onChange, value, name }) => {
            const error = errors[name];
            return (
              <TextInput
                id="collection-title-input"
                name={name}
                value={value}
                label={t('mediapopup.gallections.input.name')}
                validationResult={error ? 'error' : undefined}
                details={error?.message}
                maxLength={MAX_TITLE_LENGTH}
                onChange={onChange}
                onKeyUp={onTitleKeyUp}
                autoFocus
              />
            );
          }}
        />
        {withPremiumForm && (
          <FormProvider {...formContext}>
            <PremiumForm />
          </FormProvider>
        )}
      </div>
    </DSModal>
  );
};
NewGallectionPopup.displayName = 'NewGallectionPopup';

const enhance = compose(
  withCurrentMediaSource,
  withCurrentGallection,
  withGruser,
  withGallections,
  withCurrentGallectionSubfolders,
  connectMediaStore(
    state => ({
      coreLevel: getCoreLevel(state),
      hasCoreMembership: getCurrentUserHasCore(state),
    }),
    {
      createGallection,
    }
  )
);

export default enhance(NewGallectionPopup) as React.FC<OwnProps>;
